import React, {useEffect, useRef} from "react"
import {Category} from "../../../../domain/category"
import * as d3 from "d3"
import {formatAmount, formatImpact} from "../../transactions/transaction-amount/transaction-amount"
import styles from "./categories-d3-chart.module.scss"
import {useWindowSize} from "../../../../components-shared/use-window-size/use-window-size"

type DataPointWithCategory = {category: Category, value: number}

interface CategoriesBarChartProps {
  dataPointsWithCategory: DataPointWithCategory[]
  clickCategory: (category: Category) => void
  viewMode: "expenses" | "co2"
}

export const CategoriesD3Chart: React.FC<CategoriesBarChartProps> = ({ dataPointsWithCategory , clickCategory, viewMode }) => {
  const ref = useRef(null)
  const [windowWidth, windowHeight] = useWindowSize()

  useEffect(() => {
    const n = dataPointsWithCategory.length
    const margin = ({top: 16, right: 6, bottom: 6, left: 0})
    const barSize = 18
    const betweenBars = 6
    const textHeight = 20
    const height = margin.top + (barSize + textHeight) * n + betweenBars * (n-1) + margin.bottom
    const spaceForValue = 80

    const chartDiv = d3.select(ref.current)

    chartDiv.select("svg").remove()

    const svg = chartDiv
      .append("svg")
      .attr("width","100%")
      .attr("height", height)

    const width = parseInt(svg.style("width"))
    const widthForBars = width - spaceForValue

    const dataset = dataPointsWithCategory
      .sort((x, y) => Math.abs(y.value) - Math.abs(x.value))
      .map(x => Math.abs(x.value))
    const max = dataset
      .reduce((previousMax, currentNumber) => Math.max(previousMax, currentNumber))
    const scaleValue = (value: number) => Math.abs(value / max * widthForBars)

    const getYForIndex = (i: number) => 5 + i*(barSize + betweenBars + textHeight)
    const formatValue = (value: number) => {
      return viewMode === "expenses"
        ? formatAmount(Math.abs(value), 0)
        : formatImpact(Math.abs(value), 0)
    }

    svg.selectAll("rect")
      .data(dataPointsWithCategory, (d) => (d as DataPointWithCategory).category.id)
      .enter()
      .append("rect")
      .attr("x", 0)
      .attr("y", (d, i) => getYForIndex(i) + textHeight)
      .attr("width", d => scaleValue(d.value))
      .attr("height", barSize)
      .attr("fill", d => d.category.color)
      .attr("fill-opacity", 0.8)
      .attr("class", styles.ClickableBar)
      .on("click", (e, d) => { clickCategory(d.category) })

    svg.selectAll("text")
      .data(dataPointsWithCategory, (d) => (d as DataPointWithCategory).category.id)
      .enter()
      .append("text")
      .attr("x", 0)
      .attr("y", (d, i) => getYForIndex(i))
      .text(d => d.category.name)
      .attr("font-weight", "bold")
      .attr("font-size", 14)
      .attr("dy", "1em")

    svg.selectAll("q")
      .data(dataPointsWithCategory, (d) => (d as DataPointWithCategory).category.id)
      .enter()
      .append("text")
      .attr("x", d => scaleValue(d.value) + 4)
      .attr("y", (d, i) => getYForIndex(i) + textHeight)
      .text(d => formatValue(d.value))
      // .attr("fill-opacity", 0.7)
      .attr("font-weight", "bold")
      .attr("font-size", 14)
      .attr("dy", "1em")

    // x-axis
    // svg.append("svg:line")
    //   .attr("x1", 0)
    //   .attr("x2", 0)
    //   .attr("y1", 0)
    //   .attr("y2", height)
    //   .attr("stroke", "#000")

    // svg.selectAll("q")
    //   .data(dataPointsWithCategory)
    //   .enter()
    //   .append("rect")
    //   .attr("x", 0)
    //   .attr("y", (d, i) => getYForIndex(i) + textHeight + barSize)
    //   .attr("width", d => scaleValue(d.value) / 2)
    //   .attr("height", barSize)
    //   .attr("fill", d => d.category.color)
    //   .attr("fill-opacity", 0.3)
    //
    // svg.selectAll("q")
    //   .data(dataPointsWithCategory)
    //   .enter()
    //   .append("text")
    //   .attr("x", d => scaleValue(d.value) / 2 + 4)
    //   .attr("y", (d, i) => getYForIndex(i) + textHeight + barSize)
    //   .text(d => formatValue(d.value / 2))
    //   .attr("fill-opacity", 0.5)
    //   .attr("font-weight", "bold")
    //   .attr("font-size", 14)
    //   .attr("dy", "1em")

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataPointsWithCategory, viewMode, windowWidth, windowHeight])

  return <div ref={ref}>
  </div>
}

